import merge from 'lodash/merge'
import { THEME } from './enums'

const COLORS = [
  // Base
  'white',
  'black',

  // Brand
  'primary',
  'primaryDarker',
  'primaryDisabled',
  'secondary',
  'secondaryDarker',
  'secondaryDisabled',
  'third',

  // Neutrals
  'neutral',
  'neutralDarker',
  'neutralDisabled',
  'neutralLight',

  // Text
  'fontMain',
  'fontSecondary',
  'fontMuted',

  // Backgrounds
  'backgroundMain',
  'backgroundPrimary',
  'backgroundSecondary',
  'backgroundHover',
  'backgroundGrayLight',
  'backgroundGrayDark',
  'backgroundGrayDarkAccent',
  'backgroundPrimaryTint',
  'backgroundMuted',

  // Borders
  'border',
  'borderSecondary',

  // Overlays & Shadows
  'overlay',
  'shadowLight',
  'shadowMedium',

  // Status
  'accent',
  'focus',
  'info',
  'warning',
  'warningSecondary',
  'success',
  'successSecondary',
  'error',
  'errorSecondary',

  // Special
  'gradient',
  'saved',
  'bookingReserved',
] as const
export type Colors = typeof COLORS[number]
export function isColor(key: string): key is Colors {
  return COLORS.includes(key as Colors)
}

const colors: Record<Colors, string> = {
  // Base
  white: 'white',
  black: '#000000',

  // Brand
  primary: '#1600FF',
  primaryDarker: '#1202B7',
  primaryDisabled: 'rgba(22, 0, 255, 0.5)',
  secondary: '#F45353',
  secondaryDarker: '#EC1C1C',
  secondaryDisabled: 'rgba(244, 83, 83, 0.5)',
  third: '#00E7D7',

  // Neutrals
  neutral: '#9098A1',
  neutralDarker: '#858C94',
  neutralDisabled: 'rgba(144, 152, 161, 0.7)',
  neutralLight: '#BDBBBC',

  // Text
  fontMain: '#231F20',
  fontSecondary: '#595959',
  fontMuted: '#8F8F8F',

  // Backgrounds
  backgroundMain: '#F8F8F8',
  backgroundPrimary: '#FFFFFF',
  backgroundSecondary: '#F6F4F3',
  backgroundHover: '#F5F5F5',
  backgroundGrayLight: '#F4F6F9',
  backgroundGrayDark: '#C4C4C4',
  backgroundGrayDarkAccent: '#798087',
  backgroundPrimaryTint: '#F3F2FF',
  backgroundMuted: '#E3E4EB',

  // Borders
  border: '#F0EFF0',
  borderSecondary: '#D8D8D8',

  // Overlays & Shadows
  overlay: 'rgba(35, 31, 32, 0.4)',
  shadowLight: 'rgba(0, 0, 0, 0.05)',
  shadowMedium: 'rgba(152, 142, 142, 0.75)',

  // Status
  accent: '#FFF5D6',
  focus: '#00A79D',
  info: '#2E5AAC',
  warning: '#B95000',
  warningSecondary: '#FFF4EC',
  success: '#287D3C',
  successSecondary: '#EDF9F0',
  error: '#DA1414',
  errorSecondary: '#FEEFEF',

  // Special
  gradient: '#00A79C',
  saved: '#16A34A',
  bookingReserved: '#88E8B0',
}

//Type and Const List for Fonts
export const FONTS = ['main', 'secondary'] as const
export type Font = typeof FONTS[number]

//Fonts for Text Properties
export const fontsMapped: Record<Font, string> = {
  main: 'Work Sans',
  secondary: 'Poppins',
}

export const fonts = {
  main: 'Work Sans',
  secondary: 'Poppins',
}

export const baseTheme = {
  colors,
  fonts,
}

const lightTheme = { ...baseTheme }

const darkTheme = merge({}, baseTheme, {
  // TODO: implement dark theme
})

export const themes = {
  [THEME.Light]: lightTheme,
  [THEME.Dark]: darkTheme,
}

export interface IBaseTheme extends Readonly<typeof baseTheme> {}

declare module 'styled-components' {
  export interface DefaultTheme extends IBaseTheme {}
}
